import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService } from '../catalog/catalog.service';
import { IdentityAdminService } from '../identity/identity-admin.service';
import {
  CreateQuotaConsumptionPayload,
  CreateSalesQuotaPayload,
  QuotaConsumptionRecord,
  SalesApiService,
  SalesChannelRecord,
  UpdateSalesQuotaPayload
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-quota-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TableModule, DialogModule, CurrencySelectorComponent,
    CustomerSelectorComponent, JalaliDateInputComponent, AinDatePipe, DecimalPipe
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.quotas.form.createTitle') : t('sales.quotas.form.editTitle')" [description]="t('sales.quotas.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <header class="card header">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/quotas']">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
          </div>
          <div class="summary">
            <span>{{ t('sales.quotas.fields.code') }}: <strong>{{ form.code || '-' }}</strong></span>
            <span>{{ t('sales.quotas.fields.scopeType') }}: <strong>{{ scopeLabel(form.scopeType) }}</strong></span>
            <span>{{ t('sales.quotas.fields.limitValue') }}: <strong>{{ form.limitValue }}</strong></span>
            @if (!isCreateMode()) {
              <span>{{ t('sales.quotas.utilization.consumed') }}: <strong>{{ consumedTotal() }}</strong></span>
              <span>{{ t('sales.quotas.utilization.remaining') }}: <strong>{{ remainingTotal() }}</strong></span>
            }
          </div>
          @if (!isCreateMode()) {
            <div class="progress-wrap">
              <div class="progress-bar" [style.width.%]="utilizationPercent()"></div>
            </div>
          }
        </header>

        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.quotas.fields.code') }}</span><input pInputText [(ngModel)]="form.code" /></label>
            <label><span>{{ t('sales.quotas.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
            <label>
              <span>{{ t('sales.quotas.fields.scopeType') }}</span>
              <p-select [options]="scopeOptions" optionValue="value" [(ngModel)]="form.scopeType" appendTo="body" (ngModelChange)="form.scopeId = ''">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('sales.quotas.fields.scopeTarget') }}</span>
              @if (form.scopeType === 'Channel') {
                <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.scopeId" appendTo="body" />
              } @else if (form.scopeType === 'Customer') {
                <ain-customer-selector [(ngModel)]="form.scopeId" />
              } @else if (form.scopeType === 'Product') {
                <p-select [options]="offeringOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.scopeId" [filter]="true" appendTo="body" />
              } @else {
                <p-select [options]="userOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.scopeId" appendTo="body" />
              }
            </label>
            <label>
              <span>{{ t('sales.quotas.fields.metricType') }}</span>
              <p-select [options]="metricOptions" optionValue="value" [(ngModel)]="form.metricType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            @if (form.metricType === 'Amount') {
              <label><span>{{ t('sales.quotas.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            }
            <label>
              <span>{{ t('sales.quotas.fields.periodType') }}</span>
              <p-select [options]="periodOptions" optionValue="value" [(ngModel)]="form.periodType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            @if (form.periodType === 'FixedRange') {
              <label><span>{{ t('sales.quotas.fields.periodStart') }}</span><ain-date-input [(ngModel)]="form.periodStartUtc" /></label>
              <label><span>{{ t('sales.quotas.fields.periodEnd') }}</span><ain-date-input [(ngModel)]="form.periodEndUtc" /></label>
            }
            <label><span>{{ t('sales.quotas.fields.limitValue') }}</span><p-inputNumber [(ngModel)]="form.limitValue" [min]="0" /></label>
          </section>
        } @else {
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('sales.quotas.tabs.general') }}</button>
            <button type="button" [class.active]="activeTab() === 'consumptions'" (click)="activeTab.set('consumptions')">{{ t('sales.quotas.tabs.consumptions') }}</button>
            <button type="button" [class.active]="activeTab() === 'utilization'" (click)="activeTab.set('utilization')">{{ t('sales.quotas.tabs.utilization') }}</button>
          </div>

          @if (activeTab() === 'general') {
            <section class="card grid">
              <label><span>{{ t('sales.quotas.fields.code') }}</span><input pInputText [(ngModel)]="form.code" readonly /></label>
              <label><span>{{ t('sales.quotas.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
              <label>
                <span>{{ t('sales.quotas.fields.metricType') }}</span>
                <p-select [options]="metricOptions" optionValue="value" [(ngModel)]="form.metricType" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              @if (form.metricType === 'Amount') {
                <label><span>{{ t('sales.quotas.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
              }
              <label>
                <span>{{ t('sales.quotas.fields.periodType') }}</span>
                <p-select [options]="periodOptions" optionValue="value" [(ngModel)]="form.periodType" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              @if (form.periodType === 'FixedRange') {
                <label><span>{{ t('sales.quotas.fields.periodStart') }}</span><ain-date-input [(ngModel)]="form.periodStartUtc" /></label>
                <label><span>{{ t('sales.quotas.fields.periodEnd') }}</span><ain-date-input [(ngModel)]="form.periodEndUtc" /></label>
              }
              <label><span>{{ t('sales.quotas.fields.limitValue') }}</span><p-inputNumber [(ngModel)]="form.limitValue" [min]="0" /></label>
              <label class="check"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ t('sales.quotas.active') }}</span></label>
            </section>
          }

          @if (activeTab() === 'consumptions') {
            <section class="card">
              <div class="items-toolbar">
                <h3>{{ t('sales.quotas.consumptions.title') }}</h3>
                <button pButton type="button" severity="secondary" [label]="t('sales.quotas.consumptions.add')" (click)="showConsumptionDialog.set(true)"></button>
              </div>
              <p-table [value]="consumptions()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('sales.quotas.consumptions.value') }}</th>
                    <th>{{ t('sales.quotas.consumptions.at') }}</th>
                    <th>{{ t('sales.quotas.consumptions.referenceType') }}</th>
                    <th>{{ t('sales.quotas.consumptions.notes') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.consumedValue }}</td>
                    <td>{{ row.consumedAtUtc | ainDate:'datetime' }}</td>
                    <td>{{ row.referenceType }}</td>
                    <td>{{ row.notes || '-' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          }

          @if (activeTab() === 'utilization') {
            <section class="card utilization">
              <p>{{ t('sales.quotas.utilization.hint') }}</p>
              <div class="util-grid">
                <div><span>{{ t('sales.quotas.fields.limitValue') }}</span><strong>{{ form.limitValue }}</strong></div>
                <div><span>{{ t('sales.quotas.utilization.consumed') }}</span><strong>{{ consumedTotal() }}</strong></div>
                <div><span>{{ t('sales.quotas.utilization.remaining') }}</span><strong>{{ remainingTotal() }}</strong></div>
                <div><span>{{ t('sales.quotas.utilization.percent') }}</span><strong>{{ utilizationPercent() | number:'1.0-1' }}%</strong></div>
              </div>
            </section>
          }
        }

        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>

    <p-dialog [header]="t('sales.quotas.consumptions.add')" [visible]="showConsumptionDialog()" (visibleChange)="showConsumptionDialog.set($event)" [modal]="true" [style]="{ width: '420px' }">
      <div class="dialog-grid">
        <label><span>{{ t('sales.quotas.consumptions.value') }}</span><p-inputNumber [(ngModel)]="consumptionForm.consumedValue" [min]="0" /></label>
        <label><span>{{ t('sales.quotas.consumptions.at') }}</span><ain-date-input [(ngModel)]="consumptionForm.consumedAtUtc" /></label>
        <label><span>{{ t('sales.quotas.consumptions.notes') }}</span><input pInputText [(ngModel)]="consumptionForm.notes" /></label>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="showConsumptionDialog.set(false)"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="saveConsumption()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 10px; }
    .header .summary { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 10px; font-size: 13px; color: #475569; }
    .progress-wrap { margin-top: 10px; height: 8px; background: #e2e8f0; border-radius: 999px; overflow: hidden; }
    .progress-bar { height: 100%; background: #1d4ed8; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .tabs { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .tabs button { border: 1px solid #cbd5e1; background: #fff; border-radius: 999px; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
    .items-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
    .items-toolbar h3 { margin: 0; font-size: 15px; }
    .util-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
    .util-grid div { display: grid; gap: 4px; padding: 10px; border: 1px solid #e2e8f0; border-radius: 10px; }
    .util-grid span { color: #64748b; font-size: 12px; }
    .dialog-grid { display: grid; gap: 10px; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid, .util-grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesQuotaDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly catalog = inject(CatalogService);
  private readonly identityApi = inject(IdentityAdminService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly activeTab = signal<'general' | 'consumptions' | 'utilization'>('general');
  readonly consumptions = signal<QuotaConsumptionRecord[]>([]);
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly userOptions = signal<{ label: string; value: string }[]>([]);
  readonly showConsumptionDialog = signal(false);

  readonly scopeOptions = [
    { value: 'Seller', labelKey: 'sales.quotas.scopes.seller' },
    { value: 'Channel', labelKey: 'sales.quotas.scopes.channel' },
    { value: 'Customer', labelKey: 'sales.quotas.scopes.customer' },
    { value: 'Product', labelKey: 'sales.quotas.scopes.product' }
  ];
  readonly metricOptions = [
    { value: 'Amount', labelKey: 'sales.quotas.metrics.amount' },
    { value: 'Quantity', labelKey: 'sales.quotas.metrics.quantity' }
  ];
  readonly periodOptions = [
    { value: 'Daily', labelKey: 'sales.quotas.periods.daily' },
    { value: 'Weekly', labelKey: 'sales.quotas.periods.weekly' },
    { value: 'Monthly', labelKey: 'sales.quotas.periods.monthly' },
    { value: 'Quarterly', labelKey: 'sales.quotas.periods.quarterly' },
    { value: 'Yearly', labelKey: 'sales.quotas.periods.yearly' },
    { value: 'FixedRange', labelKey: 'sales.quotas.periods.fixedRange' }
  ];

  form = {
    code: '',
    name: '',
    scopeType: 'Channel',
    scopeId: '',
    metricType: 'Amount',
    currencyCode: 'IRR',
    periodType: 'Monthly',
    periodStartUtc: null as string | null,
    periodEndUtc: null as string | null,
    limitValue: 0,
    isActive: true
  };

  consumptionForm = {
    consumedValue: 0,
    consumedAtUtc: null as string | null,
    notes: ''
  };

  ngOnInit(): void {
    this.api.listChannels().subscribe({
      next: rows => this.channels.set(rows),
      error: () => this.channels.set([])
    });
    this.catalog.listOfferings().subscribe({
      next: rows => this.offerings.set(rows),
      error: () => this.offerings.set([])
    });
    this.identityApi.listUsers({ skip: 0, take: 300 }).subscribe({
      next: page => this.userOptions.set((page.items ?? []).map(user => ({
        label: user.displayName || user.email || user.id,
        value: user.id
      }))),
      error: () => this.userOptions.set([])
    });

    const quotaId = this.route.snapshot.paramMap.get('quotaId');
    this.isCreateMode.set(!quotaId || quotaId === 'new');
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }
    this.loadQuota(quotaId!);
  }

  channelOptions(): { label: string; value: string }[] {
    return this.channels().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  offeringOptions(): { label: string; value: string }[] {
    return this.offerings().map(item => ({ label: `${item.code} - ${item.title}`, value: item.id }));
  }

  consumedTotal(): number {
    return this.consumptions().reduce((sum, item) => sum + item.consumedValue, 0);
  }

  remainingTotal(): number {
    return Math.max(0, this.form.limitValue - this.consumedTotal());
  }

  utilizationPercent(): number {
    if (this.form.limitValue <= 0) return 0;
    return Math.min(100, (this.consumedTotal() / this.form.limitValue) * 100);
  }

  loadQuota(id: string): void {
    this.api.getQuota(id).subscribe({
      next: quota => {
        this.form = {
          code: quota.code,
          name: quota.name,
          scopeType: quota.scopeType,
          scopeId: quota.scopeId,
          metricType: quota.metricType,
          currencyCode: quota.currencyCode ?? 'IRR',
          periodType: quota.periodType,
          periodStartUtc: quota.periodStartUtc ?? null,
          periodEndUtc: quota.periodEndUtc ?? null,
          limitValue: quota.limitValue,
          isActive: quota.isActive
        };
        this.loadConsumptions(id);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.quotas.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  loadConsumptions(id: string): void {
    this.api.listQuotaConsumptions(id).subscribe({
      next: rows => this.consumptions.set(rows),
      error: () => this.consumptions.set([])
    });
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');

    if (this.isCreateMode()) {
      const payload: CreateSalesQuotaPayload = {
        code: this.form.code,
        name: this.form.name,
        scopeType: this.form.scopeType,
        scopeId: this.form.scopeId,
        metricType: this.form.metricType,
        currencyCode: this.form.metricType === 'Amount' ? this.form.currencyCode : null,
        periodType: this.form.periodType,
        periodStartUtc: this.form.periodStartUtc,
        periodEndUtc: this.form.periodEndUtc,
        limitValue: this.form.limitValue
      };
      this.api.createQuota(payload).subscribe({
        next: quota => void this.router.navigate(['/sales/quotas', quota.id]),
        error: () => this.failSave()
      });
      return;
    }

    const id = this.route.snapshot.paramMap.get('quotaId')!;
    const payload: UpdateSalesQuotaPayload = {
      name: this.form.name,
      metricType: this.form.metricType,
      currencyCode: this.form.metricType === 'Amount' ? this.form.currencyCode : null,
      periodType: this.form.periodType,
      periodStartUtc: this.form.periodStartUtc,
      periodEndUtc: this.form.periodEndUtc,
      limitValue: this.form.limitValue,
      isActive: this.form.isActive
    };
    this.api.updateQuota(id, payload).subscribe({
      next: () => {
        this.saving.set(false);
        void this.router.navigate(['/sales/quotas']);
      },
      error: () => this.failSave()
    });
  }

  saveConsumption(): void {
    const quotaId = this.route.snapshot.paramMap.get('quotaId')!;
    const payload: CreateQuotaConsumptionPayload = {
      consumedValue: this.consumptionForm.consumedValue,
      consumedAtUtc: this.consumptionForm.consumedAtUtc,
      referenceType: 'Manual',
      notes: this.consumptionForm.notes || null
    };
    this.api.createQuotaConsumption(quotaId, payload).subscribe({
      next: () => {
        this.showConsumptionDialog.set(false);
        this.consumptionForm = { consumedValue: 0, consumedAtUtc: null, notes: '' };
        this.loadConsumptions(quotaId);
      },
      error: () => this.errorMessage.set(this.t('sales.quotas.consumptions.errorSave'))
    });
  }

  scopeLabel(scopeType: string): string {
    return this.localization.translate(`sales.quotas.scopes.${scopeType.charAt(0).toLowerCase()}${scopeType.slice(1)}`);
  }

  private failSave(): void {
    this.errorMessage.set(this.t('sales.quotas.errorSave'));
    this.saving.set(false);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
